import { Flex, Row, Col } from "antd";
import { StatCard } from "./Partials";
import { formatMoney, Safe } from "utils/functions";
import { PaymentType, StrategyResponse } from "dtos/strategy.dto";
import { AtLeastOne } from "types/_utils.types";
import { ReactNode } from "react";

type Stats = "total_payment" | "initial_payment" | "total_debt" | "total_interest" | "tenure" | "interest_tenure";

interface OverviewSection {
  strategy: StrategyResponse | undefined;
  placement: AtLeastOne<AtLeastOne<Stats>>;
  loading?: boolean;
}

export const OverviewSection = ({ strategy, placement, loading }: OverviewSection) => {
  const frequency = strategy?.payment_type === PaymentType.MONTHLY ? "month" : "week";

  const StatRegistry: Record<Stats, ReactNode> = {
    total_payment: (
      <StatCard
        loading={loading}
        label="Payable Amount"
        text={formatMoney(strategy?.common.total_payable, true)}
        subtext={
          strategy?.common.weekly_payments
            ? `${formatMoney(strategy.common.weekly_payments * (frequency === "month" ? 4 : 1), false)} / ${frequency}`
            : "Full Upfront"
        }
      />
    ),
    initial_payment: (
      <StatCard
        loading={loading}
        label="Initial Payment"
        text={formatMoney(strategy?.common.initial_payment, true)}
        subtext={
          strategy?.common.initial_amount
            ? `${formatMoney(strategy.common.initial_amount, false)} to be paid upfront`
            : strategy?.common.initial_program_fee
              ? `${formatMoney(strategy.common.initial_program_fee, false)} interest`
              : undefined
        }
      />
    ),
    total_debt: (
      <StatCard
        loading={loading}
        label="Total Debt"
        text={formatMoney(strategy?.common.total_debt, true)}
        subtext={
          strategy?.payment_type === PaymentType.ONE_TIME
            ? "One-Time payment"
            : Math.max(...(strategy?.common.split_rows.creditor_max_weekly ?? []))
              ? `${formatMoney(Safe(Math.max(...(strategy?.common.split_rows.creditor_max_weekly ?? []))).multiply(frequency === "month" ? 4 : 1).value, false)} / ${frequency}`
              : "No payment"
        }
      />
    ),
    total_interest: (
      <StatCard
        loading={loading}
        label="Net Commission"
        text={formatMoney(strategy?.common.interest, true)}
        subtext={
          strategy?.common.split_rows.program_weekly
            ? `${formatMoney(Safe(strategy?.common.split_rows.program_weekly).multiply(frequency === "month" ? 4 : 1).value, false)} / ${frequency}`
            : "One-Time payment"
        }
      />
    ),
    tenure: (
      <StatCard
        loading={loading}
        label="Tenure"
        text={
          strategy?.payment_type === PaymentType.ONE_TIME
            ? "None"
            : `${strategy?.payment_type === PaymentType.MONTHLY ? Math.floor(strategy.total_weeks / 4) : strategy?.total_weeks} ${frequency}s`
        }
        subtext={`+1 initial ${frequency}`}
      />
    ),
    interest_tenure: (
      <StatCard
        loading={loading}
        label="Program Tenure"
        text={
          strategy?.payment_type === PaymentType.ONE_TIME
            ? "None"
            : `${strategy?.payment_type === PaymentType.MONTHLY ? Math.floor(strategy.program_tenure / 4) : strategy?.program_tenure} ${frequency}s`
        }
        subtext={`+1 initial ${frequency}`}
      />
    ),
  };

  return (
    <Flex gap={16} vertical>
      {placement.map((row, rid) => (
        <Row key={`strategy_preview_overview_r${rid}`} gutter={12}>
          {row.map((col, cid) => (
            <Col key={`strategy_preview_overview_r${rid}c${cid}`} span={24 / row.length}>
              {StatRegistry[col]}
            </Col>
          ))}
        </Row>
      ))}
    </Flex>
  );
};
